import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { ProtectedRoute } from './protected-route';
import { AccountStatusGuard } from './account-status-guard';
import { useAuthStore } from '../stores/auth-store';
import { RouteAccessLevel } from '../config/route-config';
import { UserType } from '../lib/auth/account-status';
import {
    canAccessRoute,
    getRedirectPath,
    logUnauthorizedAccess,
} from '../lib/auth/rbac-utils';

interface UserTypeProtectedRouteProps {
    children: React.ReactNode;
    accessLevel: RouteAccessLevel;
    requireEmailVerified?: boolean;
    requireProfileCompleted?: boolean;
    requirePartnerVerified?: boolean;
    redirectPath?: {
        partner?: string;
        regular?: string;
    };
}

/**
 * UserTypeProtectedRoute - Three-Layer Security Component
 * 
 * Combines all three security layers:
 * 1. Authentication (via ProtectedRoute)
 * 2. Account Status (via AccountStatusGuard)
 * 3. Authorization/RBAC (user type check)
 * 
 * This is the primary route protection component for the application.
 */
export function UserTypeProtectedRoute({
    children,
    accessLevel,
    requireEmailVerified = true,
    requireProfileCompleted = false,
    requirePartnerVerified = false,
    redirectPath,
}: UserTypeProtectedRouteProps) {
    const { user, profile, profileLoaded, isAuthenticated } = useAuthStore();
    const location = useLocation();
    const toastShownRef = useRef(false);

    // Layer 1: Authentication - handled by ProtectedRoute wrapper
    // Layer 2: Account Status - handled by AccountStatusGuard wrapper
    // Layer 3: Authorization (RBAC) - handled here

    // Get user type from profile
    const userType = profile?.user_type === 'partner' ? UserType.PARTNER :
        profile?.user_type === 'regular' ? UserType.REGULAR :
            null;

    // Check if user can access this route based on access level
    const hasAccess = canAccessRoute(userType, { accessLevel } as any);

    // Log unauthorized access attempts (no toast notification)
    // IMPORTANT: ALL HOOKS must be called BEFORE any conditional returns
    useEffect(() => {
        // Only execute the logic when access is denied AND profile is loaded
        // AND we haven't logged yet
        if (!hasAccess && profileLoaded && isAuthenticated && !toastShownRef.current) {
            toastShownRef.current = true;

            // Log unauthorized access attempt (for debugging/analytics)
            logUnauthorizedAccess(
                user?.id,
                userType,
                location.pathname,
                accessLevel
            );

            // Silent redirect - no toast notification
        }

        // Reset ref when access is granted or path changes
        if (hasAccess) {
            toastShownRef.current = false;
        }
    }, [hasAccess, profileLoaded, isAuthenticated, user?.id, userType, location.pathname, accessLevel]);

    // Wait for profile only when a session exists. Guests have no profile to
    // load — blocking here is the infinite spinner on public / leftover-gated routes.
    if (isAuthenticated && !profileLoaded) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
        );
    }

    // If the user is authenticated but has no profile record (or profile has no name),
    // redirect them to the profile completion page before letting them into the app.
    // Partners are excluded — their profile is handled via /partner/complete-profile.
    // /name-step and /profile/complete are also excluded — they ARE the completion flows.
    const PROFILE_COMPLETION_BYPASS_PATHS = ['/name-step', '/profile/complete', '/partner/complete-profile'];
    const isOnCompletionPath = PROFILE_COMPLETION_BYPASS_PATHS.some(p => location.pathname.startsWith(p));
    if (isAuthenticated && profileLoaded && (!profile || !profile.fullname?.trim()) && !isOnCompletionPath) {
        const isPartner = profile?.user_type === 'partner';
        if (!isPartner) {
            return <Navigate to="/profile/complete" replace />;
        }
    }

    const gatedContent = (
        <ProtectedRoute>
            <AccountStatusGuard
                requireEmailVerified={requireEmailVerified}
                requireProfileCompleted={requireProfileCompleted}
                requirePartnerVerified={requirePartnerVerified}
                bypassForPaths={[
                    '/partner/verify-email',
                    '/partner/verification-pending',
                    '/partner/verification-rejected',
                    '/partner/complete-profile',
                    '/account-suspended',
                    '/verify-email',
                ]}
            >
                {children}
            </AccountStatusGuard>
        </ProtectedRoute>
    );

    // Not signed in: show global sign-in dialog (ProtectedRoute) instead of a blind redirect
    if (!hasAccess && !isAuthenticated) {
        return gatedContent;
    }

    // Signed in but wrong role / tier — redirect using RBAC config.
    // EXCEPTION: Completion paths (/name-step, /profile/complete) are always accessible
    // to authenticated users regardless of profile state. A brand-new user has profile=null
    // which makes userType=null, which makes canAccessRoute(AUTHENTICATED) return false.
    // Without this bypass, new users get redirected to /sign-in immediately after OTP.
    if (!hasAccess && !isOnCompletionPath) {
        let redirect: string;

        if (redirectPath) {
            if (userType === UserType.PARTNER && redirectPath.partner) {
                redirect = redirectPath.partner;
            } else if (userType === UserType.REGULAR && redirectPath.regular) {
                redirect = redirectPath.regular;
            } else {
                redirect = getRedirectPath(userType, location.pathname);
            }
        } else {
            redirect = getRedirectPath(userType, location.pathname);
        }

        return <Navigate to={redirect} replace />;
    }

    return gatedContent;
}
